import { studyQueryKeys } from '@/config/queryKeys';
import { TABLE_PAGE_SIZE_OPTIONS } from '@/constants';
import { useDateFormatter } from '@/hooks';
import { createAgGridDatasource } from '@/lib/createAgGridDatasource';
import { searchStudySamples } from '@/services/samples';
import { PaginatedSamplesResponse, SampleSearchRequest, StudyAssay, StudyPublicGetOne, StudySample } from '@/types';
import { flushColumnState } from '@/utils/flushColumnState';
import { Button } from '@nautilusbio/nb-design-system/Button';
import { Stack } from '@nautilusbio/nb-design-system/Stack';
import { Table } from '@nautilusbio/nb-design-system/Table';
import { Text } from '@nautilusbio/nb-design-system/Text';
import { useNavigate } from '@tanstack/react-router';
import { ColDef, GridOptions } from 'ag-grid-enterprise';
import { AgGridReact } from 'ag-grid-react';
import React, { useEffect, useMemo, useRef } from 'react';
import { useTranslation } from 'react-i18next';

import { useSampleTableGrid } from '../../Studies/components/useSampleTableGrid';
import { TEXT_FILTER_PARAMS } from '../../Studies/utils/sharedColumnDefs';

interface AssaySamplesTableProps {
  orgId: string;
  studyId: string;
  assaySummary: StudyAssay;
  studySummary: Pick<StudyPublicGetOne, 'num_proteins' | 'num_proteoform_groups'>;
}

const paginationPageSizeKey = (studyId: string) => `study-${studyId}-assaySamples-pageSize`;
const VALID_SORT_FIELDS = ['name', 'instrument_run_name', 'created_at', 'updated_at', 'well_position', 'demux_index'];
const DEFAULT_PAGE_SIZE = 25;

const gridOptions: GridOptions = {
  cacheBlockSize: 100,
  maxBlocksInCache: 10,
  suppressMovableColumns: false,
  suppressDragLeaveHidesColumns: false,
};

export const AssaySamplesTable = function AssaySamplesTable({
  assaySummary,
  orgId,
  studyId,
  studySummary,
}: AssaySamplesTableProps) {
  const { t } = useTranslation('studies');
  const navigate = useNavigate();
  const formatDate = useDateFormatter();
  const gridRef = useRef<AgGridReact<StudySample>>(null);
  const isTau = assaySummary.assay_version === 'Tau';
  const isBroadscale = assaySummary.assay_version === 'Broadscale';
  const hasDetailRoute = isBroadscale || isTau;
  const assayLabel = assaySummary.assay_version ?? t('columnHeaders.assay');
  const tableId = `assay-samples-table-${assaySummary.assay_version ?? 'unknown'}`;

  const datasource = useMemo(
    () =>
      createAgGridDatasource<StudySample, PaginatedSamplesResponse, SampleSearchRequest>(
        studyQueryKeys.endpoints.samplesSearch(orgId, studyId),
        ({ limit, skip, sortBy, sortOrder, filters }) => {
          const validSortBy = sortBy && VALID_SORT_FIELDS.includes(sortBy) ? sortBy : 'name';
          // Lock results to this assay version; merge with any user-applied column filters
          const assayVersionFilter = assaySummary.assay_version
            ? {
                conditions: [{ operator: 'equals' as const, value: assaySummary.assay_version }],
                logic: 'and' as const,
              }
            : undefined;

          return {
            skip,
            limit,
            sort_by: validSortBy as SampleSearchRequest['sort_by'],
            sort_order: (sortOrder ?? 'asc') as 'asc' | 'desc',
            filters: {
              ...filters,
              assay_version: assayVersionFilter,
            } as SampleSearchRequest['filters'],
          };
        },
        {
          fetcher: request => searchStudySamples(orgId, studyId, request),
        },
      ),
    [orgId, studyId, assaySummary.assay_version],
  );

  const {
    defaultColDef,
    handleFilterChanged,
    handleFirstDataRendered,
    handleGridReady,
    handleModelUpdated,
    handlePaginationChanged,
    pageSize,
  } = useSampleTableGrid<StudySample>({
    studyId,
    tableId,
    validSortFields: VALID_SORT_FIELDS,
    paginationPageSizeKeyFn: paginationPageSizeKey,
    defaultPageSize: DEFAULT_PAGE_SIZE,
    datasource,
    autoHeight: true,
  });

  const colDefs: ColDef<StudySample>[] = useMemo(
    () => [
      {
        field: 'name',
        headerName: t('columnHeaders.sampleName'),
        filter: 'agTextColumnFilter',
        filterParams: TEXT_FILTER_PARAMS,
        resizable: true,
        sortable: true,
      },
      {
        field: 'sample_group',
        headerName: t('columnHeaders.sampleGroup'),
        resizable: true,
        sortable: false,
        filter: false,
      },
      ...(isBroadscale
        ? [
            {
              field: 'num_proteins',
              headerName: t('columnHeaders.proteinsDetected'),
              resizable: true,
              sortable: false,
              // TODO: enable agNumberColumnFilter once SampleFilters supports num_proteins: NumericFieldFilters
              filter: false,
              valueFormatter: ({ value }: { value: number }) => value?.toLocaleString() ?? '0',
            } as unknown as ColDef<StudySample>,
          ]
        : []),
      ...(isTau
        ? [
            {
              field: 'num_proteoform_groups',
              headerName: t('columnHeaders.proteoformCount'),
              resizable: true,
              sortable: false,
              // TODO: enable agNumberColumnFilter once SampleFilters supports num_proteoform_groups: NumericFieldFilters
              filter: false,
              valueFormatter: ({ value }: { value: number }) => value?.toLocaleString() ?? '0',
            } as unknown as ColDef<StudySample>,
            {
              field: 'num_ptms',
              headerName: 'PTMs',
              resizable: true,
              sortable: false,
              // TODO: enable agNumberColumnFilter once SampleFilters supports num_ptms: NumericFieldFilters
              filter: false,
              valueFormatter: ({ value }: { value: number }) => value?.toLocaleString() ?? '0',
            } as unknown as ColDef<StudySample>,
            {
              field: 'num_isoforms',
              headerName: 'Isoforms',
              resizable: true,
              sortable: false,
              // TODO: enable agNumberColumnFilter once SampleFilters supports num_isoforms: NumericFieldFilters
              filter: false,
              valueFormatter: ({ value }: { value: number }) => value?.toLocaleString() ?? '0',
            } as unknown as ColDef<StudySample>,
          ]
        : []),
      {
        field: 'instrument_run_name',
        headerName: t('columnHeaders.instrumentRun'),
        filter: 'agTextColumnFilter',
        filterParams: TEXT_FILTER_PARAMS,
        resizable: true,
        sortable: true,
      },
      {
        field: 'species',
        headerName: t('columnHeaders.species'),
        resizable: true,
        sortable: false,
        filter: false,
      },
      {
        field: 'created_at',
        headerName: t('columnHeaders.created'),
        resizable: true,
        sortable: true,
        filter: false,
        valueFormatter: ({ value }) => (value ? formatDate(value) : ''),
      },
    ],
    [isBroadscale, isTau, t, formatDate],
  );

  useEffect(() => {
    if (gridRef.current?.api) {
      gridRef.current.api.setGridOption('serverSideDatasource', datasource);
    }
  }, [datasource]);

  const handleDetailsClick = () => {
    if (isBroadscale) {
      navigate({
        to: '/studies/$studyId/broadscale-samples',
        params: { studyId },
      });
    } else if (isTau) {
      navigate({ to: '/studies/$studyId/tau-samples', params: { studyId } });
    }
  };

  return (
    <Stack fullWidth gap={4} orientation="vertical">
      <Stack alignItems="center" fullWidth justifyContent="space-between" orientation="horizontal">
        <Stack alignItems="center" gap={4} orientation="horizontal">
          <Text as="h3">{assayLabel} Samples</Text>
          {hasDetailRoute ? (
            <Button data-testid="assay-samples-detail-link" size="sm" visual="unstyled" onPress={handleDetailsClick}>
              {t('study.assayDetailLink')}
            </Button>
          ) : null}
        </Stack>
        <Stack gap={4} orientation="horizontal">
          <Text weight="bold">Total:</Text>
          <Text>{assaySummary.num_samples}</Text>
          {isBroadscale && (
            <>
              <Text weight="bold">{t('study.proteinsQuantified')}:</Text>
              <Text>{studySummary.num_proteins}</Text>
            </>
          )}
          {isTau && (
            <>
              <Text weight="bold">{t('columnHeaders.proteoformCount')}:</Text>
              <Text>{studySummary.num_proteoform_groups}</Text>
            </>
          )}
        </Stack>
      </Stack>
      <Table
        columnDefs={colDefs}
        defaultColDef={defaultColDef}
        domLayout="autoHeight"
        enableAdvancedFilter={false}
        enableResponsiveResize={false}
        getRowId={(params: { data: StudySample }) => params.data.id}
        gridOptions={gridOptions}
        onFilterChanged={handleFilterChanged}
        onFirstDataRendered={handleFirstDataRendered}
        onGridPreDestroyed={e => flushColumnState(e.api, tableId)}
        onGridReady={handleGridReady}
        onModelUpdated={handleModelUpdated}
        onPaginationChanged={handlePaginationChanged}
        pagination
        paginationPageSize={pageSize}
        paginationPageSizeSelector={TABLE_PAGE_SIZE_OPTIONS}
        ref={gridRef}
        resizeDebounceMs={150}
        rowModelType="serverSide"
        tableId={tableId}
      />
    </Stack>
  );
};
